import { Button } from '@components/Button';
import Center from '@components/Center/Center';
import PropertyCard from '@components/PropertiesSearch/PropertyCard';
import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import { Listing } from '@engelvoelkersamericas/api';
import { CircularProgress } from '@mui/material';
import { useTranslations } from 'next-intl';
import { FC, ReactNode } from 'react';
import CardGridWithMobileCarousel from '..';
import { CarouselCtaWrapper, CarouselHeaderWrapper, CarouselHeadline } from '../style';

interface FeaturedPropertyCardGridCarouselProps {
  header?: ReactNode;
  listings: Listing[];
  searchLink: boolean;
  swatch?: string;
  ctaUrl?: string | null;
  ctaUrlTarget?: boolean;
  ctaText?: string;
  customBtns: any[];
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  refreshProperties?: ({
    listingId,
    listOfficeMlsId,
    remove,
  }: {
    listingId: string;
    listOfficeMlsId: string;
    remove: boolean;
  }) => void;
  dataTestId?: string;
}

const FeaturedPropertyCardGridCarousel: FC<FeaturedPropertyCardGridCarouselProps> = ({
  header,
  listings,
  searchLink,
  swatch,
  ctaUrl,
  ctaUrlTarget,
  ctaText,
  customBtns,
  loading = false,
  emptyMessage,
  className,
  dataTestId,
  refreshProperties,
}) => {
  const t = useTranslations();

  const cardElements = listings.map((listing, idx) => {
    return (
      <PropertyCard
        dataTestId={`property-listing-card-${idx}`}
        listing={listing}
        idx={idx}
        refreshProperties={refreshProperties}
      />
    );
  });

  return (
    <div data-testid={dataTestId}>
      <Block
        className={className}
        background={swatch === 'cream' ? 'secondaryBg' : 'bg'}
        narrow={swatch === 'cream'}
      >
        <CarouselHeaderWrapper>
          {header && <CarouselHeadline data-testid={`listings-header`}>{header}</CarouselHeadline>}
          {searchLink && (
            <Button
              url={'/'}
              label={t('Search New Location')}
              variant="tertiaryGray"
              arrow={false}
              icon={'Magnifier'}
              iconPosition={'right'}
            />
          )}
        </CarouselHeaderWrapper>

        {header && <VerticalLineSeparator />}

        <CardGridWithMobileCarousel
          dataTestId={dataTestId}
          cardArr={cardElements}
          cardType="property"
        />

        {loading && (
          <Center>
            <CircularProgress color="inherit" />
          </Center>
        )}
        {emptyMessage && !loading && !listings.length && <Center>{emptyMessage}</Center>}

        <CarouselCtaWrapper>
          {customBtns &&
            customBtns.map((item) => {
              return (
                item.show != false && (
                  <Button
                    key={item.label}
                    url={item.url}
                    label={t(item.label || 'Missing Label')}
                    variant="secondary"
                    onClickHandler={item.callback}
                    arrow={false}
                  />
                )
              );
            })}

          {ctaUrl && (
            <Button
              url={ctaUrl}
              target={ctaUrlTarget}
              dataTestId="view-all-listings-btn"
              label={ctaText ?? t('View All Listings')}
              variant="secondary"
              arrow={false}
            />
          )}
        </CarouselCtaWrapper>
      </Block>
    </div>
  );
};

export default FeaturedPropertyCardGridCarousel;
